import { showSuccessNotification, showWarningNotification } from '../../../../widgets/notificationToaster';
import React, { Suspense, useEffect } from 'react';
import { useLazyLoadQuery, useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import { ErrorPage } from '../../../_error';
import Loader from 'src/components/widgets/loader';
import { FullscreenLayout } from 'src/components/widgets/fullscreenIframeLayout';
import { useSaveOptimizedScene, useSaveProduct } from 'src/components/widgets/preview3D/hooks/useSaveProduct';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { withTimeout } from 'src/shared/withTimeout';
import styled, { css } from 'styled-components';
import LightsIcon from 'src/shared/material_icons/action/lightbulb.svg';
import ObjectIcon from 'src/shared/material_icons/action/group_work.svg';
import AreasIcon from 'src/shared/material_icons/maps/category.svg';
import PrintingIcon from 'src/shared/material_icons/action/print.svg';
import { T, toLocalID } from '../../../../../shared/helpers';
import { useProductConfigurationState } from '../../state';
import ComposerAttributeContentPage from '../configurator-components/composer-editors/attributes/composerAttributePage';
import {
    ComposerAttributeNode,
    ComposerAttributeType,
    ComposerEcommercePlatform
} from '../configurator-components/composer-editors/attributes/composerAttributesModels';
import AttributesEditor from '../configurator-components/composer-editors/attributesEditor';
import ComposerPlaceholderContentPage from '../configurator-components/composer-editors/composerPlaceholderContentPage';
import { ComposerResizeHandle } from '../configurator-components/composer-editors/composerWorkspace';
import CustomizableAreasEditor from '../configurator-components/composer-editors/customizableAreasEditor';
import PredesignTemplateEditor from '../configurator-components/composer-editors/predesignTemplateEditor';
import PrintingEditor from '../configurator-components/composer-editors/printingEditor';
import ComposerSettings from '../configurator-components/composer-editors/settings/composerSettings';
import { ComposerTemplateContentPage } from '../configurator-components/composer-editors/templates/composerTemplateContentPage';
import HeaderToolbar from '../configurator-components/headerToolbar';
import LeftVerticalToolbar, { StepsTools } from '../configurator-components/leftVerticalToolbar';
import { composerQueriesEcommerceSettingsQuery } from './composer/__generated__/composerQueriesEcommerceSettingsQuery.graphql';
import { composerQueriesFontQuery } from './composer/__generated__/composerQueriesFontQuery.graphql';
import { composerQueriesListTemplatesQuery } from './composer/__generated__/composerQueriesListTemplatesQuery.graphql';
import {
    composerEcommerceSettingsQuery,
    composerExtensionFieldsQuery,
    composerFontQuery,
    composerListTemplatesQuery,
    composerMerchantOptionsQuery,
    composerPrintTypesQuery
} from './composer/composerQueries';
import useComposerStore from './composer/composerStore';
import { applyDesignPreviews } from './composer/helpers/composerDesignPreview';
import { getComposerValidationMessages } from './composer/helpers/composerAttributeValidation';
import { createComposerDirtySnapshot } from './composer/helpers/composerDirtySnapshot';
import { ComposerAttributeNodeAdapter, ComposerTemplateNodeAdapter } from './composer/helpers/composerNodesAdapter';
import useSaveDesign from './composer/helpers/useSaveDesign';

const Container = styled(FullscreenLayout)`
    --w-panel-min: 284px;
    --composer-rail-width: 88px;
    --composer-structure-min: calc(var(--w-panel-min) + var(--composer-rail-width));
    --composer-app: #eff3f7;
    --composer-panel: #fff;
    --composer-sunken: #f4f7fa;
    --composer-hover: #e9eff5;
    --composer-line: #dce3eb;
    --composer-line-soft: #eaeff4;
    --composer-fg: #1e2a38;
    --composer-muted: #5c6b7a;
    --composer-faint: #677078;
    --composer-accent: #e8650a;
    --composer-accent-deep: #c25309;
    --composer-accent-text: #a8480b;
    --composer-accent-wash: #fdf0e6;
    --composer-kind-group: #7040d0;
    --composer-kind-attribute: #0e7c7c;
    --composer-kind-option: #b32d7d;
    --composer-ai: #6b36ce;
    --composer-ai-wash: #f7f3fe;
    --composer-shadow: rgba(30, 42, 56, 0.05) 0 1px 2px, rgba(30, 42, 56, 0.06) 0 0 0 1px;
    --c1: 2fr;
    --c2: 2fr;
    --c3: 3fr;
    --c4: 3fr;
    --c234: 8fr;

    color: var(--composer-fg);
    background: var(--composer-app);
    font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        'Segoe UI',
        system-ui,
        sans-serif;
    font-size: 13px;
    z-index: 30;

    &,
    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }

    & > * {
        width: 100%;
    }
`;

const MainContentContainer = styled.div`
    flex: 1;
    overflow: hidden;
    padding: 0 6px 6px;
    box-sizing: border-box;
`;

const MainContentLayout = styled.div<{ $structureWidth?: number }>`
    display: grid;
    grid-template-columns: ${(props) => (props.$structureWidth ? `${props.$structureWidth}px` : 'minmax(var(--composer-structure-min), var(--c1))')} 6px minmax(
            0,
            var(--c234)
        );
    gap: 0;
    height: 100%;
    max-height: 100%;
    background: var(--composer-app);

    @media (max-width: 940px) {
        grid-template-columns: 1fr;

        > :first-child,
        > [role='separator'] {
            display: none;
        }
    }
`;

const PreviewContainer = styled.div`
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const LoadingState = styled.div`
    position: absolute;
    inset: 0;
    z-index: 40;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(239, 243, 247, 0.9);
    backdrop-filter: blur(2px);
`;

const LoadingCard = styled.div`
    width: min(420px, 100%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 28px;
    border: 1px solid var(--composer-line);
    border-radius: 10px;
    background: var(--composer-panel);
    box-shadow: 0 16px 45px rgba(30, 42, 56, 0.16);
    text-align: center;
`;

const LoadingTitle = styled.strong`
    color: var(--composer-fg);
    font-size: 16px;
`;

const LoadingDescription = styled.span`
    color: var(--composer-muted);
    line-height: 1.5;
`;

const LeftVerticalToolbarContainer = styled.div<{ $hidden?: boolean; $hiddenSidebar?: boolean }>`
    min-width: 0;
    overflow: hidden;
    ${(props) =>
        props.$hidden &&
        css`
            display: none;
        `}

    ${(props) =>
        props.$hiddenSidebar &&
        css`
            border-right: none;
        `}
`;

const STRUCTURE_MIN_WIDTH = 372;
const COMPOSER_SAVE_TIMEOUT_MS = 120_000;
const COMPOSER_REQUIRED_DATA_TIMEOUT_MS = 45_000;

const asError = (error: unknown): Error => {
    if (error instanceof Error) return error;
    if (Array.isArray(error)) return new Error(error.map((item) => item?.message ?? String(item)).join(', '));
    return new Error(typeof error === 'string' ? error : T._('An unexpected error occurred while saving the Composer.', 'Admin'));
};

const reconcileComposerAttributeNodes = (nodes: ComposerAttributeNode[], product: MPlaza.Model) => {
    const findBy = (collection: any, key: string, value: unknown) => collection?.find?.((item: any) => item.get?.(key) === value);
    for (const node of nodes) {
        const collection =
            node.type === ComposerAttributeType.Attribute
                ? product.get('attributes')
                : node.type === ComposerAttributeType.AttributeGroup
                  ? product.get('attributeGroups')
                  : product.get('attributeSteps');
        const guidKey =
            node.type === ComposerAttributeType.Attribute
                ? 'attributeGuid'
                : node.type === ComposerAttributeType.AttributeGroup
                  ? 'groupGuid'
                  : 'stepGuid';
        const idKey =
            node.type === ComposerAttributeType.Attribute ? 'attributeID' : node.type === ComposerAttributeType.AttributeGroup ? 'groupID' : 'stepID';
        const model = findBy(collection, guidKey, node.id);
        if (!model) continue;

        node.idNumeric = model.get(idKey) ?? node.idNumeric;
        node.isNew = false;
        node.data = model.toJSON();
        if (node.type !== ComposerAttributeType.Attribute) {
            if (node.type === ComposerAttributeType.AttributeGroup) node.img = model.get('imageUrl') || '';
            continue;
        }

        for (const optionNode of node.options) {
            const option = findBy(model.get('options'), 'optionGuid', optionNode.id);
            if (!option) continue;
            optionNode.idNumeric = option.get('optionID') ?? optionNode.idNumeric;
            optionNode.isNew = false;
            optionNode.img = option.get('imageUrl') || '';
            optionNode.data = option.toJSON();
            for (const actionNode of optionNode.actions) {
                const action = findBy(option.get('actions'), 'actionGuid', actionNode.id);
                if (action) {
                    actionNode.isNew = false;
                    actionNode.data = action.toJSON();
                }
            }
        }
        for (const pricingNode of node.pricings) {
            const pricing = model.get('pricings')?.find?.((item: any) =>
                pricingNode.selectedOptionGuid
                    ? item.get?.('selectedOptionGuid') === pricingNode.selectedOptionGuid
                    : item.get?.('selectedValue') === pricingNode.selectedValue && item.get?.('pricingID') === pricingNode.pricingID
            );
            if (pricing) pricingNode.attributePricingID = pricing.get?.('attributePricingID') ?? pricing.attributePricingID;
        }
    }
};

const Composer = () => {
    const { state, unsavedChanges } = useProductConfigurationState();
    const [zkProduct, zkScene, sceneEditor, setZkProduct, incrementSaveVersion] = usePreview3DStore((x) => [
        x.zkProduct,
        x.zkScene,
        x.sceneEditor,
        x.setZkProduct,
        x.incrementSaveVersion
    ]);
    const [saveProduct] = useSaveProduct();
    const [saveOptimizedScene] = useSaveOptimizedScene();
    const [saveDesign] = useSaveDesign();
    const relayEnvironment = useRelayEnvironment();
    const fetchedEcommerceSettings = useLazyLoadQuery<composerQueriesEcommerceSettingsQuery>(
        composerEcommerceSettingsQuery,
        {},
        {
            fetchPolicy: 'store-or-network'
        }
    );

    const fetchedTemplates = useLazyLoadQuery<composerQueriesListTemplatesQuery>(
        composerListTemplatesQuery,
        {
            productId: toLocalID(state.product.id) ?? 0
        },
        {
            fetchPolicy: 'store-or-network'
        }
    );

    const queryFont = useLazyLoadQuery<composerQueriesFontQuery>(
        composerFontQuery,
        {},
        {
            fetchPolicy: 'store-or-network'
        }
    );

    const [
        storedAttributes,
        storedTemplates,
        storedAreas,
        storedDesigns,
        setModel,
        setFonts,
        initializeStoreAttributeInfo,
        initializeEcommerceSettings,
        initializeStoreTemplateInfo,
        setSceneMaterials,
        setSceneObjects,
        setSceneCameras,
        setRequiredEditorData,
        validateAttributes
    ] = useComposerStore((x) => [
        x.attributes,
        x.templates,
        x.areas,
        x.designs,
        x.setModel,
        x.setFonts,
        x.intializeAttributesInfo,
        x.setEcommerceSettings,
        x.initializeTemplateInfo,
        x.setSceneMaterialList,
        x.setSceneObjectList,
        x.setSceneCameraList,
        x.setRequiredEditorData,
        x.validateAttributes
    ]);

    const [currentTab, setCurrentTab] = React.useState<string>('attributes');
    const [structureWidth, setStructureWidth] = React.useState<number>();
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [requiredEditorDataReady, setRequiredEditorDataReady] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
    const [savingMessage, setSavingMessage] = React.useState<string>(T._('Preparing the Composer save...', 'Admin'));
    const [saveError, setSaveError] = React.useState<Error | null>(null);
    const composerReadyRef = React.useRef(false);
    const composerSnapshotRef = React.useRef('');
    const saveInFlightRef = React.useRef<Promise<boolean> | null>(null);

    useEffect(() => {
        let disposed = false;
        composerReadyRef.current = false;
        setIsLoading(true);
        setRequiredEditorDataReady(false);

        const loadRequiredEditorData = async () => {
            try {
                const [merchantOptionsResult, printTypesResult, extensionFieldsResult] = await withTimeout(
                    Promise.allSettled([
                        fetchQuery<any>(
                            relayEnvironment,
                            composerMerchantOptionsQuery,
                            { salesChannelId: state.salesChannelID, sku: state.product.code },
                            { fetchPolicy: 'store-or-network' }
                        ).toPromise(),
                        fetchQuery<any>(relayEnvironment, composerPrintTypesQuery, {}, { fetchPolicy: 'store-or-network' }).toPromise(),
                        fetchQuery<any>(relayEnvironment, composerExtensionFieldsQuery, {}, { fetchPolicy: 'store-or-network' }).toPromise()
                    ]),
                    COMPOSER_REQUIRED_DATA_TIMEOUT_MS,
                    T._('The Composer support data took too long to load.', 'Admin')
                );

                if (disposed) return;

                const merchantProductOptions =
                    merchantOptionsResult.status === 'fulfilled'
                        ? (merchantOptionsResult.value?.merchantProductOptions ?? []).flatMap((option: any) =>
                              option?.optionID == null
                                  ? []
                                  : [
                                        {
                                            optionID: String(option.optionID),
                                            label: option.label || String(option.optionID),
                                            values: (option.values ?? []).flatMap((value: any) =>
                                                value?.valueID == null ? [] : [{ valueID: String(value.valueID), label: value.label || String(value.valueID) }]
                                            )
                                        }
                                    ]
                          )
                        : [];
                const merchantProductOptionsError =
                    merchantOptionsResult.status === 'rejected' || merchantOptionsResult.value == null
                        ? T._('Store attributes could not be loaded. Local attributes remain available; retry by reloading the Composer.', 'Admin')
                        : null;
                const printTypes =
                    printTypesResult.status === 'fulfilled'
                        ? (printTypesResult.value?.printTypes?.edges ?? []).flatMap((edge: any) =>
                              edge?.node?.printTypeID != null
                                  ? [{ printTypeID: Number(edge.node.printTypeID), name: edge.node.name || String(edge.node.printTypeID) }]
                                  : []
                          )
                        : [];
                const extensionFieldDefinitions =
                    extensionFieldsResult.status === 'fulfilled'
                        ? (extensionFieldsResult.value?.extensionFields ?? []).flatMap((field: any) =>
                              field?.fieldID != null
                                  ? [
                                        {
                                            id: field.id,
                                            fieldID: Number(field.fieldID),
                                            name: field.name || '',
                                            label: field.label || '',
                                            value: '',
                                            dataTypeID: field.dataTypeID ?? 0,
                                            dataTypeName: field.dataTypeName,
                                            operationTypeID: field.operationTypeID ?? 0,
                                            operationTypeName: field.operationTypeName,
                                            formatString: field.formatString || ''
                                        }
                                    ]
                                  : []
                          )
                        : [];

                setRequiredEditorData({ merchantProductOptions, merchantProductOptionsError, printTypes, extensionFieldDefinitions });
            } catch {
                if (disposed) return;
                setRequiredEditorData({
                    merchantProductOptions: [],
                    merchantProductOptionsError: T._(
                        'Store attributes could not be loaded. Local attributes remain available; retry by reloading the Composer.',
                        'Admin'
                    ),
                    printTypes: [],
                    extensionFieldDefinitions: []
                });
            } finally {
                if (!disposed) setRequiredEditorDataReady(true);
            }
        };

        void loadRequiredEditorData();
        return () => {
            disposed = true;
        };
    }, [relayEnvironment, setRequiredEditorData, state.product.code, state.salesChannelID]);

    useEffect(
        () =>
            useComposerStore.subscribe((current, previous) => {
                if (!composerReadyRef.current) return;
                if (
                    current.attributes !== previous.attributes ||
                    current.templates !== previous.templates ||
                    current.areas !== previous.areas ||
                    current.designs !== previous.designs ||
                    current.productQuantity !== previous.productQuantity ||
                    current.ecommerceSettings !== previous.ecommerceSettings
                ) {
                    const currentSnapshot = createComposerDirtySnapshot(current);
                    setHasUnsavedChanges(currentSnapshot !== composerSnapshotRef.current);
                }
            }),
        []
    );

    useEffect(() => {
        if (!zkProduct || !zkScene || !fetchedTemplates || !queryFont || !requiredEditorDataReady) return;

        composerReadyRef.current = false;

        setModel(zkProduct);

        setFonts(queryFont);

        const ecommerceSettingsData = fetchedEcommerceSettings?.ecommerceSettings;
        initializeEcommerceSettings({
            ecommercePlatform: ecommerceSettingsData?.ecommerceID ?? (-1 as ComposerEcommercePlatform)
        });

        const modelAttributes = zkProduct.get('attributes') || [];
        const attributes = modelAttributes.map(ComposerAttributeNodeAdapter.toNode);
        const steps = (zkProduct.get('attributeSteps') || []).map(ComposerAttributeNodeAdapter.toStepNode);
        const groups = (zkProduct.get('attributeGroups') || []).map(ComposerAttributeNodeAdapter.toGroupNode);

        // The API already persists displayOrder. Re-numbering the combined attributes/steps/groups
        // collection here used to reverse the tree at every reload and also mixed unrelated sibling
        // scopes. Keep the server order intact; moves normalize only the siblings they actually touch.
        const composerAttributes = [ComposerAttributeNodeAdapter.adjustValues(attributes), steps, groups].flat();
        initializeStoreAttributeInfo(modelAttributes, composerAttributes);

        const templates =
            fetchedTemplates?.designTemplates?.map((te) =>
                ComposerTemplateNodeAdapter.toNode(toLocalID(te.id) ?? 0, te.name, te.design, null, zkProduct, zkProduct.get('colors').at(0)?.get('sides'))
            ) || [];
        const areas =
            zkProduct
                .get('colors')
                .at(0)
                ?.get('sides')
                .map((side) => ComposerTemplateNodeAdapter.toNodeArea(side, zkProduct.get('scenes').at(0).get('meshSides'))) || [];
        initializeStoreTemplateInfo(templates, areas);
        void validateAttributes().finally(() => {
            composerSnapshotRef.current = createComposerDirtySnapshot(useComposerStore.getState());
            unsavedChanges.resetSnapshot();
            composerReadyRef.current = true;
            setHasUnsavedChanges(false);
            setIsLoading(false);
        });
    }, [zkProduct, zkScene, fetchedTemplates, queryFont, fetchedEcommerceSettings, requiredEditorDataReady]);

    useEffect(() => {
        if (!sceneEditor || !sceneEditor?.scene?.meshes) return;

        setSceneObjects(sceneEditor!.scene!.meshes);
        setSceneMaterials(sceneEditor!.scene!.materials);
        setSceneCameras(sceneEditor!.scene!.cameras);
    }, [sceneEditor]);

    const ComposerTools: StepsTools[] = [
        {
            tool: T._('Attributes', 'Admin'),
            icon: <ObjectIcon />,
            tooltip: T._('Attributes', 'Admin'),
            onClick: () => {
                setCurrentTab('attributes');
            },
            component: AttributesEditor
        },
        // { MERGED WITH PREDESIGN-TEMPLATES
        // 	tool: 'customizable-area',
        // 	icon: <MaterialsIcon />,
        // 	tooltip: T._('Customizable Area', 'Admin'),
        // 	onClick: () => {
        // 		setCurrentTab('customizable-area');
        // 	},
        // 	component: CustomizableAreasEditor
        // },
        {
            tool: T._('Areas', 'Admin'),
            icon: <AreasIcon />,
            tooltip: T._('Areas', 'Admin'),
            onClick: () => {
                setCurrentTab('areas');
            },
            component: CustomizableAreasEditor
        },
        {
            tool: T._('Templates', 'Admin'),
            icon: <LightsIcon />,
            tooltip: T._('Templates', 'Admin'),
            onClick: () => {
                setCurrentTab('predesign-templates');
            },
            component: PredesignTemplateEditor
        },
        {
            tool: T._('Printing', 'Admin'),
            icon: <PrintingIcon />,
            tooltip: T._('Printing', 'Admin'),
            onClick: () => {
                setCurrentTab('printing');
            },
            component: PrintingEditor
        }
    ];

    if (isLoading) {
        return (
            <Container>
                <LoadingState role='status' aria-live='polite'>
                    <LoadingCard>
                        <Loader />
                        <LoadingTitle>{T._('Loading Composer', 'Admin')}</LoadingTitle>
                        <LoadingDescription>
                            {T._('We are loading the product structure and the 3D scene. This may take a few moments.', 'Admin')}
                        </LoadingDescription>
                    </LoadingCard>
                </LoadingState>
            </Container>
        );
    }

    const currentTabContent = () => {
        if (currentTab === 'attributes') {
            return (
                <PreviewContainer>
                    <ComposerAttributeContentPage />
                </PreviewContainer>
            );
        } else if (currentTab === 'predesign-templates') {
            return (
                <PreviewContainer>
                    <ComposerTemplateContentPage />
                </PreviewContainer>
            );
        } else if (currentTab === 'areas') {
            return (
                <PreviewContainer>
                    <ComposerPlaceholderContentPage
                        section={T._('Areas', 'Admin')}
                        listMessage={T._('No area list is connected yet.', 'Admin')}
                        detailMessage={T._('No area editor is connected yet.', 'Admin')}
                    />
                </PreviewContainer>
            );
        } else if (currentTab === 'printing') {
            return (
                <PreviewContainer>
                    <ComposerPlaceholderContentPage
                        section={T._('Printing', 'Admin')}
                        listMessage={T._('No printing-method list is connected yet.', 'Admin')}
                        detailMessage={T._('No printing editor is connected yet.', 'Admin')}
                    />
                </PreviewContainer>
            );
        } else if (currentTab === 'settings') {
            return <ComposerSettings />;
        }
        return <>test</>;
    };

    const performSave = async () => {
        console.log('Save composer');
        if (!zkProduct || !storedAttributes) return false;
        setSaveError(null);
        const validatedAttributes = await validateAttributes();
        const validationMessages = getComposerValidationMessages(validatedAttributes);
        if (validationMessages.length > 0) {
            const preview = validationMessages.slice(0, 3).join(' ');
            const remaining = validationMessages.length > 3 ? ` ${validationMessages.length - 3} more error(s).` : '';
            showWarningNotification({
                message: `${T._('Saving is blocked. Fix the Composer validation errors shown by the warning icons.', 'Admin')} ${preview}${remaining}`
            });
            return false;
        }

        setSavingMessage(T._('Saving product data...', 'Admin'));
        setIsSaving(true);
        try {
            // Generate previews before the first server mutation. A broken image/font must not
            // leave the product saved while its templates are still stale.
            const uniqueDesigns = Array.from(new Set(storedDesigns.values()));
            setSavingMessage(T._('Generating design previews...', 'Admin'));
            await withTimeout(
                applyDesignPreviews(
                    uniqueDesigns.filter((design) => !(design as any).get('isDeleted')),
                    storedAreas,
                    zkProduct
                ),
                COMPOSER_SAVE_TIMEOUT_MS,
                T._('Generating the design previews took too long. Please try again.', 'Admin')
            );

            setSavingMessage(T._('Saving product data...', 'Admin'));
            console.log('Attributes to process:', validatedAttributes);
            const { attributes, groups, steps } = ComposerAttributeNodeAdapter.saveAttributes(validatedAttributes);
            console.log('Attributes to save:', attributes, groups, steps);
            zkProduct.set('attributes', attributes);
            zkProduct.set('attributeGroups', groups);
            zkProduct.set('attributeSteps', steps);

            const { sides, modelSceneMeshSides } = ComposerTemplateNodeAdapter.saveTemplates(storedTemplates, storedAreas);

            zkProduct.get('scenes').at(0).set('meshSides', modelSceneMeshSides);
            zkProduct.get('colors').at(0).set('sides', sides);

            setZkProduct(zkProduct);
            console.log('Saving product:', zkProduct);
            await saveProduct({});

            // The server consumes temporary upload keys and assigns numeric IDs. Keep the
            // editor nodes in sync immediately so a later scene/design failure can be retried
            // without recreating attributes/options or resubmitting an already-consumed image.
            reconcileComposerAttributeNodes(validatedAttributes, zkProduct);
            for (const area of storedAreas) {
                area.sideID = area.originalSide.get('sideID');
                area.originalMeshSides?.forEach((meshSide) => meshSide.set('sideID', area.sideID));
            }

            setSavingMessage(T._('Exporting and saving the 3D scene...', 'Admin'));
            await saveOptimizedScene();

            for (const [index, design] of uniqueDesigns.entries()) {
                setSavingMessage(`${T._('Saving designs...', 'Admin')} ${index + 1}/${uniqueDesigns.length}`);
                console.log('Saving design:', design);
                await withTimeout(saveDesign(design), COMPOSER_SAVE_TIMEOUT_MS, T._('Saving a design took too long. Please try again.', 'Admin'));
            }
            incrementSaveVersion();
            composerSnapshotRef.current = createComposerDirtySnapshot(useComposerStore.getState());
            setHasUnsavedChanges(false);
            unsavedChanges.resetSnapshot();
            showSuccessNotification({
                message: T._('Composer settings saved successfully', 'Admin')
            });
            return true;
        } catch (error) {
            console.error('Error during save:', error);
            setSaveError(asError(error));
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const onSave = () => {
        // React state is not synchronous: two clicks in the same frame used to start two
        // create/update sequences. Coalesce them on the exact same promise instead.
        if (saveInFlightRef.current) return saveInFlightRef.current;
        const operation = performSave().finally(() => {
            if (saveInFlightRef.current === operation) saveInFlightRef.current = null;
        });
        saveInFlightRef.current = operation;
        return operation;
    };

    if (saveError) {
        return (
            <Container>
                <ErrorPage
                    error={saveError}
                    title={T._('The Composer could not be saved', 'Admin')}
                    description={T._(
                        'Your changes are still available in this page. You can return to the Composer or try the save again. If the problem continues, reload the page and contact support.',
                        'Admin'
                    )}
                    primaryAction={{ label: T._('Try again', 'Admin'), onClick: () => void onSave() }}
                    secondaryAction={{ label: T._('Back to Composer', 'Admin'), onClick: () => setSaveError(null) }}
                />
            </Container>
        );
    }

    const startStructureResize = (event: React.MouseEvent<HTMLDivElement>) => {
        const previousPanel = event.currentTarget.previousElementSibling as HTMLElement | null;
        if (!previousPanel) return;
        const startX = event.clientX;
        const startWidth = previousPanel.getBoundingClientRect().width;
        const move = (moveEvent: MouseEvent) => setStructureWidth(Math.max(STRUCTURE_MIN_WIDTH, Math.min(720, startWidth + moveEvent.clientX - startX)));
        const stop = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', stop);
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', stop);
    };

    return (
        <Container>
            <HeaderToolbar
                activeMode='composer'
                hasUnsavedChanges={hasUnsavedChanges}
                isSaving={isSaving}
                onSave={onSave}
                onSettings={() => setCurrentTab('settings')}
            />
            <MainContentContainer>
                <MainContentLayout $structureWidth={structureWidth}>
                    <Suspense fallback={<Loader />}>
                        <LeftVerticalToolbarContainer $hidden={false} $hiddenSidebar={currentTab === 'settings'}>
                            <LeftVerticalToolbar tools={ComposerTools} hiddenSidebar={currentTab === 'settings'} variant='composer' />
                        </LeftVerticalToolbarContainer>
                        <ComposerResizeHandle
                            aria-label={T._('Resize structure column', 'Admin')}
                            aria-orientation='vertical'
                            onDoubleClick={() => setStructureWidth(undefined)}
                            onKeyDown={(event) => {
                                if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                                event.preventDefault();
                                const currentWidth =
                                    (event.currentTarget.previousElementSibling as HTMLElement | null)?.getBoundingClientRect().width ?? STRUCTURE_MIN_WIDTH;
                                setStructureWidth(Math.max(STRUCTURE_MIN_WIDTH, Math.min(720, currentWidth + (event.key === 'ArrowRight' ? 16 : -16))));
                            }}
                            onMouseDown={startStructureResize}
                            role='separator'
                            tabIndex={0}
                            title={T._('Drag to resize · double-click to reset', 'Admin')}
                        />
                        {currentTabContent()}
                    </Suspense>
                </MainContentLayout>
            </MainContentContainer>
            {isSaving && (
                <LoadingState role='status' aria-live='polite'>
                    <LoadingCard>
                        <Loader />
                        <LoadingTitle>{T._('Saving Composer', 'Admin')}</LoadingTitle>
                        <LoadingDescription>{savingMessage}</LoadingDescription>
                        <LoadingDescription>{T._('Keep this page open until the operation is complete.', 'Admin')}</LoadingDescription>
                    </LoadingCard>
                </LoadingState>
            )}
        </Container>
    );
};
export default Composer;
